/**
 * Suspensions resource: read hooks and write helpers over the
 * `profiles.suspensions` oRPC subresource.
 *
 * `useUserSuspensions` reads any profile's suspensions (moderator view, admin
 * fields present); `useMySuspensions` reads the current user's own (no admin
 * fields); `useIsSuspended` and `useSuspenseIsSuspended` read whether a
 * profile is blocked, without a `Datastore`.
 * `setBlockStatusAsync` handles the manual block/unblock control by appending a
 * typed entry via the single `create` procedure and invalidating the affected
 * list. (Warn/unwarn entries are written server-side by the `moderation.judge`
 * handler, so there is no client warn helper.)
 *
 * The wire represents timestamps as ISO strings; this layer converts them back
 * to epoch-ms so the returned `SuspensionObject`s stay identical to the legacy
 * shape and `formatSuspensions` (in `util/suspension`) needs no changes.
 */
import type { IsoLocale } from '@repo/shared/locale';
import type { SuspensionAdmin } from '@repo/shared/orpc/procedures/suspensions';
import { isoToMs } from '@repo/shared/orpc/schemas';
import { useQuery, useSuspenseQueries } from '@tanstack/react-query';
import { useSiloLanguage } from 'component/translation';
import { useMemo } from 'react';
import { useApiQueryUtils } from 'system/api-client-context';
import { type Datastore, usePersonaKey, useSiloKey } from 'util/datastore';
import type { SuspensionObject } from 'util/suspension';
import { formatSuspensions, SuspensionStatusType } from 'util/suspension';

import { siloParamsOf } from './silo-params';

function toSuspensionObject(entry: SuspensionAdmin): SuspensionObject {
    const suspension: SuspensionObject = {
        key: entry.suspensionKey,
        time: isoToMs(entry.createdAt),
        recipientKey: entry.profileKey,
        suspensionType: entry.suspensionType as SuspensionStatusType,
    };
    if (entry.from !== undefined) suspension.from = entry.from;
    if (entry.blockedByName !== undefined) suspension.blockedByName = entry.blockedByName;
    if (entry.expiresAt !== undefined) suspension.expiresAt = isoToMs(entry.expiresAt);
    if (entry.instanceKey !== undefined) suspension.instanceKey = entry.instanceKey;
    if (entry.structureKey !== undefined) suspension.structureKey = entry.structureKey;
    if (entry.objectKey !== undefined) suspension.objectKey = entry.objectKey;
    if (entry.objectType !== undefined) suspension.objectType = entry.objectType;
    return suspension;
}

function toSuspensionMap(items: SuspensionAdmin[]): Record<string, SuspensionObject> {
    return Object.fromEntries(items.map((entry) => [entry.suspensionKey, toSuspensionObject(entry)]));
}

/**
 * Any profile's suspensions, keyed by suspension key. A moderator reading
 * another user gets the admin fields (`from`/`blockedByName`); a self-read
 * omits them.
 */
export function useUserSuspensions({ siloKey, profileKey, locale }: { siloKey: string; profileKey: string; locale: IsoLocale }): {
    suspensions: Record<string, SuspensionObject>;
    isLoading: boolean;
} {
    const utils = useApiQueryUtils();
    const { data, isLoading } = useQuery({
        ...utils.profiles.suspensions.list.queryOptions({
            input: {
                siloKey,
                profileKey,
                locale,
            },
        }),
        enabled: !!profileKey,
    });

    const suspensions = useMemo(() => (data ? toSuspensionMap(data.items) : {}), [data]);
    return { suspensions, isLoading };
}

/** The current user's own suspensions, without moderator-only fields. */
export function useMySuspensions(): { suspensions: Record<string, SuspensionObject>; isLoading: boolean } {
    const siloKey = useSiloKey();
    const personaKey = usePersonaKey();
    const locale = useSiloLanguage();
    return useUserSuspensions({ siloKey, profileKey: personaKey ?? '', locale });
}

/**
 * Whether the profile's latest block action is a block. `undefined` until its
 * suspensions load, and `false` without a profile key.
 */
export function useIsSuspended({
    siloKey,
    profileKey,
    locale,
}: {
    siloKey: string;
    profileKey: string | null;
    locale: IsoLocale;
}): boolean | undefined {
    const { suspensions, isLoading } = useUserSuspensions({ siloKey, profileKey: profileKey ?? '', locale });

    if (isLoading) {
        return undefined;
    }

    return isBlocked(suspensions);
}

/**
 * Whether the profile's latest block action is a block, suspending until its
 * suspensions load. `false` without a profile key.
 */
export function useSuspenseIsSuspended({
    siloKey,
    profileKey,
    locale,
}: {
    siloKey: string;
    profileKey: string | null;
    locale: IsoLocale;
}): boolean {
    const utils = useApiQueryUtils();
    const profileKeys = profileKey ? [profileKey] : [];
    return useSuspenseQueries({
        queries: profileKeys.map((key) =>
            utils.profiles.suspensions.list.queryOptions({
                input: {
                    siloKey,
                    profileKey: key,
                    locale,
                },
                select: (data) => isBlocked(toSuspensionMap(data.items)),
            }),
        ),
        combine: (results) => results[0]?.data ?? false,
    });
}

function isBlocked(suspensions: Record<string, SuspensionObject>): boolean {
    return formatSuspensions(suspensions).latestBlock?.suspensionType === SuspensionStatusType.BLOCKED;
}

async function invalidateSuspensionsAsync(datastore: Datastore, profileKey: string) {
    await datastore.getQueryClient().invalidateQueries({
        queryKey: datastore
            .getApiQueryUtils()
            .profiles.suspensions.list.queryOptions({ input: { siloKey: datastore.getSiloKey(), profileKey } }).queryKey,
    });
}

/**
 * Refreshes the cached suspensions for a user. The judgement flow needs this
 * because the warn/block write now happens server-side in `moderation.judge`
 * rather than through a client mutation, so nothing else invalidates the list.
 */
export async function invalidateUserSuspensionsAsync({ datastore, userKey }: { datastore: Datastore; userKey: string }) {
    await invalidateSuspensionsAsync(datastore, userKey);
}

export async function setBlockStatusAsync({ datastore, userKey, value }: { datastore: Datastore; userKey: string; value: boolean }) {
    await datastore.getApiClient().profiles.suspensions.create({
        ...siloParamsOf(datastore),
        profileKey: userKey,
        suspensionType: value ? 'blocked' : 'unblocked',
    });
    await invalidateSuspensionsAsync(datastore, userKey);
}
